/**
 * Hooks de TanStack Query para el admin de pedidos de tienda fisica.
 *
 * Patron alineado con `useStoreMaterials.ts`: dbClient.rpc(...) +
 * normalizeRpcResponse. Reutiliza el sistema existente. La unica diferencia
 * es que las RPCs admin de pedidos requieren `p_admin_user_id` explicito,
 * obtenido del contexto de auth.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import { useAuth } from '@/hooks/useAuth';
import { refreshStoreAfter } from '../../storeRefresh';
import { isIsoDate, ORDER_PAGE_SIZE } from '../orderListModel';
import type {
    StoreOrderListItem,
    StoreOrderDetail,
    StoreOrdersStats,
    SyncStripeCatalogResult,
} from '../types';

const QK = {
    stats:  ['admin', 'store-orders', 'stats'] as const,
    detail: (id: string | null) =>
        ['admin', 'store-orders', 'detail', id] as const,
};

export interface StoreOrdersListParams {
    view: string;
    q: string;
    page: number;
    sort: string | null;
    dir: 'asc' | 'desc' | null;
    origin?: string;
    from?: string;
    to?: string;
    minCents?: number | null;
    limit?: number;
    offset?: number;
}

export interface StoreOrderViewCounts {
    [viewId: string]: number;
}

function rpcParams(userId: string, query: StoreOrdersListParams): Record<string, unknown> {
    const params: Record<string, unknown> = {
        p_admin_user_id: userId,
        p_view: query.view,
        p_limit: query.limit ?? ORDER_PAGE_SIZE,
        p_offset: query.offset ?? (Math.max(query.page, 1) - 1) * ORDER_PAGE_SIZE,
    };
    const search = query.q.trim();
    if (search) params.p_search = search;
    if (query.origin) params.p_origin = query.origin;
    if (query.from && isIsoDate(query.from)) params.p_date_from = query.from;
    if (query.to && isIsoDate(query.to)) params.p_date_to = query.to;
    if (query.minCents != null) params.p_min_total_cents = query.minCents;
    if (query.sort) {
        params.p_sort = query.sort;
        params.p_sort_dir = query.dir === 'desc' ? 'desc' : 'asc';
    }
    return params;
}

function asCount(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
    if (value && typeof value === 'object') {
        const entries = Object.values(value as Record<string, unknown>);
        if (entries.length === 1) return asCount(entries[0]);
    }
    return 0;
}

export async function fetchStoreOrders(
    userId: string,
    query: StoreOrdersListParams,
): Promise<{ rows: StoreOrderListItem[]; total: number }> {
    const { data, error } = await dbClient.rpc<StoreOrderListItem[]>(
        'admin_list_store_orders',
        rpcParams(userId, query),
    );
    if (error) throw new Error(error.message);
    const rows = normalizeRpcResponse(data) as StoreOrderListItem[];
    const rawTotal = rows[0]?.total_count;
    return { rows, total: rawTotal == null ? rows.length : asCount(rawTotal) };
}

export function useStoreOrdersStats() {
    const { user } = useAuth();
    return useQuery<StoreOrdersStats | null>({
        queryKey: QK.stats,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<StoreOrdersStats>(
                'admin_get_store_orders_stats',
                { p_admin_user_id: user!.id }
            );
            if (error) throw new Error(error.message);
            const norm = normalizeRpcResponse(data);
            return (norm[0] as StoreOrdersStats | undefined) ?? null;
        },
        staleTime: 30_000,
    });
}

export function useStoreOrdersList(query: StoreOrdersListParams) {
    const { user } = useAuth();
    return useQuery({
        queryKey: ['admin', 'store-orders', 'list', query] as const,
        enabled: Boolean(user?.id),
        queryFn: () => fetchStoreOrders(user!.id, query),
        placeholderData: keepPreviousData,
        staleTime: 30_000,
    });
}

export function useStoreOrderViewCounts(query: Pick<StoreOrdersListParams, 'q' | 'origin' | 'from' | 'to' | 'minCents'>) {
    const { user } = useAuth();
    return useQuery<StoreOrderViewCounts>({
        queryKey: ['admin', 'store-orders', 'views', query] as const,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const params: Record<string, unknown> = { p_admin_user_id: user!.id };
            const search = query.q.trim();
            if (search) params.p_search = search;
            if (query.origin) params.p_origin = query.origin;
            if (query.from && isIsoDate(query.from)) params.p_date_from = query.from;
            if (query.to && isIsoDate(query.to)) params.p_date_to = query.to;
            if (query.minCents != null) params.p_min_total_cents = query.minCents;
            const { data, error } = await dbClient.rpc<{ view_id: string; total: number | string }[]>(
                'admin_store_order_view_counts',
                params,
            );
            if (error) throw new Error(error.message);
            const counts: StoreOrderViewCounts = {};
            for (const row of normalizeRpcResponse(data)) {
                if (row?.view_id) counts[row.view_id] = asCount(row.total);
            }
            return counts;
        },
        staleTime: 30_000,
    });
}

export function useStoreOrderDetail(orderId: string | null) {
    const { user } = useAuth();
    return useQuery<StoreOrderDetail | null>({
        queryKey: QK.detail(orderId),
        enabled: Boolean(orderId && user?.id),
        queryFn: async () => {
            if (!orderId) return null;
            const { data, error } = await dbClient.rpc<StoreOrderDetail>(
                'admin_get_store_order_detail',
                { p_admin_user_id: user!.id, p_order_id: orderId }
            );
            if (error) throw new Error(error.message);
            const norm = normalizeRpcResponse(data);
            return (norm[0] as StoreOrderDetail | undefined) ?? null;
        },
        staleTime: 15_000,
    });
}

export interface UpdateOrderInput {
    orderId: string;
    newStatus: 'paid' | 'processing' | 'shipped' | 'delivered' | 'completed' | 'cancelled';
    note?: string;
    trackingCarrier?: string;
    trackingNumber?: string;
    trackingUrl?: string;
    cancellationReason?: string;
}

export function useUpdateStoreOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: UpdateOrderInput) => {
            const { data, error } = await apiClient.post('/store/admin/update-order', input);
            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => refreshStoreAfter(qc, 'orders'),
    });
}

function useSelectedOrderMutation(functionName: string) {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (orderIds: string[]) => {
            if (!user?.id) throw new Error('Sesión no válida');
            const { data, error } = await dbClient.rpc(functionName, {
                p_admin_user_id: user.id,
                p_body: JSON.stringify(orderIds),
            });
            if (error) throw new Error(error.message);
            return asCount(data);
        },
        onSuccess: () => refreshStoreAfter(qc, 'orders'),
    });
}

export function useCancelSelectedStoreOrders() {
    return useSelectedOrderMutation('admin_cancel_selected_store_orders');
}

export function useCompleteSelectedStoreOrders() {
    return useSelectedOrderMutation('admin_complete_selected_store_orders');
}

export interface RefundOrderInput {
    orderId: string;
    reason?: string;
    returns?: Array<{ itemId: string; returned: boolean }>;
}

export function useResendStoreOrderEmails() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (orderId: string) => {
            const { data, error } = await apiClient.post('/store/admin/resend-order-emails', { orderId });
            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'store-orders'] });
        },
    });
}

export function useCancelStoreOrderLine() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input: { itemId: string; note?: string }) => {
            if (!user?.id) throw new Error('Sin sesión admin');
            const { error } = await dbClient.rpc('admin_cancel_store_order_line', {
                p_admin_user_id: user.id,
                p_item_id: input.itemId,
                p_note: input.note ?? '',
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => refreshStoreAfter(qc, 'orders'),
    });
}

export function useRefundStoreOrder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: RefundOrderInput) => {
            const { data, error } = await apiClient.post('/store/admin/refund', input);
            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => refreshStoreAfter(qc, 'orders'),
    });
}

export interface SyncStripeInput {
    variantId?: string;
}

export function useSyncStripeCatalog() {
    return useMutation<SyncStripeCatalogResult, Error, SyncStripeInput>({
        mutationFn: async (input: SyncStripeInput = {}) => {
            const { data, error } = await apiClient.post<SyncStripeCatalogResult>(
                '/store/admin/sync-stripe',
                input
            );
            if (error) throw new Error(error.message);
            return data ?? {};
        },
    });
}
